import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OpportunityService, type OpportunityFilters } from '../services/opportunityService';
import type { OpportunityCreateInput, OpportunityUpdateInput } from '../types/opportunity';

export const opportunityKeys = {
  list: (organizationId: string, filters: OpportunityFilters) =>
    ['opportunities', organizationId, filters] as const,
  detail: (organizationId: string, id: string) =>
    ['opportunity', organizationId, id] as const,
  outcomeCounts: (organizationId: string, opportunityIds: string[]) =>
    ['opportunity-outcomes', organizationId, opportunityIds] as const,
  outcomes: (organizationId: string, opportunityId: string) =>
    ['opportunity-outcomes', organizationId, opportunityId] as const,
};

function invalidateOpportunityQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
) {
  queryClient.invalidateQueries({ queryKey: ['opportunities', organizationId] });
  queryClient.invalidateQueries({ queryKey: ['opportunity', organizationId] });
}

function invalidateOutcomeQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
) {
  queryClient.invalidateQueries({ queryKey: ['opportunity-outcomes', organizationId] });
}

export function useOpportunities(organizationId: string, filters: OpportunityFilters) {
  return useQuery({
    queryKey: opportunityKeys.list(organizationId, filters),
    enabled: Boolean(organizationId),
    queryFn: () => OpportunityService.list(organizationId, filters),
  });
}

export function useOpportunity(organizationId: string, opportunityId: string | null) {
  return useQuery({
    queryKey: opportunityKeys.detail(organizationId, opportunityId ?? ''),
    enabled: Boolean(organizationId && opportunityId),
    queryFn: () => OpportunityService.getById(organizationId, opportunityId as string),
  });
}

export function useIssueOpportunities(organizationId: string, issueId: string | null | undefined) {
  return useQuery({
    queryKey: ['opportunities', organizationId, 'issue', issueId ?? ''],
    enabled: Boolean(organizationId && issueId),
    queryFn: () => OpportunityService.listByIssue(organizationId, issueId as string),
  });
}

export function useCreateOpportunity(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: OpportunityCreateInput) => OpportunityService.create(organizationId, input),
    onSuccess: () => invalidateOpportunityQueries(queryClient, organizationId),
  });
}

export function useUpdateOpportunity(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: OpportunityUpdateInput }) =>
      OpportunityService.update(id, input),
    onSuccess: () => invalidateOpportunityQueries(queryClient, organizationId),
  });
}

export function useDeleteOpportunity(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opportunityId: string) => OpportunityService.delete(organizationId, opportunityId),
    onSuccess: () => {
      invalidateOpportunityQueries(queryClient, organizationId);
      invalidateOutcomeQueries(queryClient, organizationId);
    },
  });
}

export function useOpportunityOutcomeCounts(organizationId: string, opportunityIds: string[]) {
  return useQuery({
    queryKey: opportunityKeys.outcomeCounts(organizationId, opportunityIds),
    enabled: Boolean(organizationId) && opportunityIds.length > 0,
    queryFn: () => OpportunityService.listOutcomeCounts(organizationId, opportunityIds),
  });
}

export function useOpportunityOutcomes(organizationId: string, opportunityId: string | null) {
  return useQuery({
    queryKey: opportunityKeys.outcomes(organizationId, opportunityId ?? ''),
    enabled: Boolean(organizationId && opportunityId),
    queryFn: () => OpportunityService.listOutcomes(organizationId, opportunityId as string),
  });
}

export function useLinkOpportunityAction(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { opportunityId: string; actionId: string }) =>
      OpportunityService.linkAction(input.opportunityId, input.actionId),
    onSuccess: () => invalidateOutcomeQueries(queryClient, organizationId),
  });
}

export function useLinkOpportunityProject(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { opportunityId: string; projectId: string }) =>
      OpportunityService.linkProject(input.opportunityId, input.projectId),
    onSuccess: () => invalidateOutcomeQueries(queryClient, organizationId),
  });
}

export function useLinkOpportunityKaizen(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { opportunityId: string; kaizenId: string }) =>
      OpportunityService.linkKaizen(input.opportunityId, input.kaizenId),
    onSuccess: () => invalidateOutcomeQueries(queryClient, organizationId),
  });
}

export function useUnlinkOpportunityOutcome(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (outcomeId: string) => OpportunityService.unlinkOutcome(outcomeId),
    onSuccess: () => invalidateOutcomeQueries(queryClient, organizationId),
  });
}
